import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  toExternalConnectionTO,
  type ExternalConnectionTO,
  type PatientProfileTO,
} from '../../lib';
import {
  ReconciliationSummaryTO,
  toReconciliationSummaryTO,
} from '../../lib/transferObjects/ReconciliationSummary';

interface Features {
  [key: string]: any;
}

interface PatientProfileState {
  patientProfile: PatientProfileTO;
  features: Features;
  wellbeingPlan: any | null;
  message: string | null;
  patientProfileStatus: boolean;
  loading: boolean;
  medicineProfileAccountActive: boolean;
  osaNavigatorAccountActive: boolean;
  myPainHealthAccountActive: boolean;
  reconciliationSummary?: ReconciliationSummaryTO[];
  externalConnections?: ExternalConnectionTO[];
}

const initialState: PatientProfileState = {
  patientProfile: {} as PatientProfileTO,
  features: {},
  wellbeingPlan: null,
  message: null,
  patientProfileStatus: false,
  loading: false,
  medicineProfileAccountActive: false,
  osaNavigatorAccountActive: false,
  myPainHealthAccountActive: false,
  reconciliationSummary: undefined,
  externalConnections: undefined,
};

export const patientProfileSlice = createSlice({
  name: 'patientProfile',
  initialState,
  reducers: {
    checkingPatientProfileStatus: (state) => {
      state.patientProfileStatus = false;
    },
    setPatientProfile: (
      state,
      {
        payload,
      }: PayloadAction<{
        patientProfile?: any;
        wellbeingPlan?: any;
        reconciliationSummary?: any;
        features?: Features;
        message?: string;
        medicineProfileAccountActive?: boolean;
        osaNavigatorAccountActive?: boolean;
        externalConnections?: any[];
      }>,
    ) => {
      state.patientProfile = payload.patientProfile || state.patientProfile;
      state.wellbeingPlan = payload.wellbeingPlan || state.wellbeingPlan;
      state.reconciliationSummary = (
        payload.reconciliationSummary ||
        state.reconciliationSummary ||
        []
      ).map((item: any) => toReconciliationSummaryTO(item));
      state.externalConnections = (
        payload.externalConnections ||
        state.externalConnections ||
        []
      ).map(toExternalConnectionTO);
      state.features = payload.features || state.features;
      state.patientProfileStatus = true;
      state.message = payload.message ?? state.message;
      state.medicineProfileAccountActive =
        payload.medicineProfileAccountActive ??
        state.medicineProfileAccountActive;
      state.osaNavigatorAccountActive =
        payload.osaNavigatorAccountActive ?? state.osaNavigatorAccountActive;
    },
    setPatientProfileState: (
      state,
      { payload }: PayloadAction<{ patientProfile: any }>,
    ) => {
      state.patientProfile = payload.patientProfile;
      state.patientProfileStatus = true;
    },
    setWellbeingPlan: (
      state,
      { payload }: PayloadAction<{ wellbeingPlan: any }>,
    ) => {
      state.wellbeingPlan = payload.wellbeingPlan;
    },
    setLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
  },
});

export const {
  checkingPatientProfileStatus,
  setPatientProfile,
  setPatientProfileState,
  setWellbeingPlan,
  setLoading,
} = patientProfileSlice.actions;
